import { CompanyType, TimesheetRow, TimesheetType, UserEntity, UserType } from '@sit/openapi';
import { SelectPropValue } from '@web/components/Fields/Select/shared/type';

export const isTimesheetReadOnly = (
  timesheet: { companyId: string } | undefined,
  user: Pick<UserType, 'companies'> | undefined,
  currentEntity?: UserEntity,
): boolean => {
  if (!timesheet) {
    return false;
  }
  const userEntity = user?.companies.find((company) => company.id === timesheet.companyId);
  const isTopLevelEntity = currentEntity?.intacctEntityId == null;

  if (!userEntity) {
    return false;
  }

  return !isTopLevelEntity && userEntity?.id !== currentEntity?.id;
};

export function isRequiredDimension(objectName: string, timesheet?: Partial<TimesheetType>, company?: Partial<CompanyType>): boolean {
  return getRequiredDimensionNames(timesheet, company).has(objectName);
}

function hasDimensionValue(values: TimesheetRow['dimensionValues'] | undefined, objectName: string): boolean {
  return Object.entries(values ?? {}).some(([key, value]) => key === objectName && !!value) || !!values?.additionalValues?.[objectName];
}

function getRequiredDimensionNames(timesheet?: Partial<TimesheetType>, company?: Partial<CompanyType>): Set<string> {
  const configuration = timesheet ?? company;
  const requiredDimensions = new Set(
    configuration?.dimensionsOrder
      ?.filter((dimension) => dimension.isFixed && dimension.isRequired)
      .map((dimension) => dimension.objectName),
  );

  // Consider removing since backend is handling this now.
  if (configuration?.employeePositionRequired) requiredDimensions.add('EMPLOYEEPOSITION');
  if (configuration?.laborClassRequired) requiredDimensions.add('LABORCLASS');
  if (configuration?.laborShiftRequired) requiredDimensions.add('LABORSHIFT');
  if (configuration?.laborUnionRequired) requiredDimensions.add('LABORUNION');

  return requiredDimensions;
}

export function hasMissingRequiredDimensionsInRows(
  rows: TimesheetRow[],
  timesheet?: Partial<TimesheetType>,
  company?: Partial<CompanyType>,
): boolean {
  const requiredDimensionNames = [...getRequiredDimensionNames(timesheet, company)];

  return rows.some((row) => {
    const hasMissingDimension = Object.values(row.entries).some((entry) => {
      if (entry.duration === null) return false;

      return requiredDimensionNames.some((objectName) => !hasDimensionValue(row.dimensionValues, objectName));
    });

    return hasMissingDimension || hasMissingRequiredDimensionsInRows(row.children ?? [], timesheet, company);
  });
}

export function getDimensionsMap(data: Pick<TimesheetRow, 'dimensionValues'>) {
  const dimensions = new Map<string, string | SelectPropValue>();
  let billable = false;

  for (const [key, value] of Object.entries(data.dimensionValues || {})) {
    if (key === 'additionalData' || key === 'additionalValues' || !value) continue;

    if (key === 'BILLABLE') {
      billable = value === 'true' || value === true;
      continue;
    }

    const valueAsStr = value as string;

    // Try and collect the properties in `additionalData`
    const additionalDataValue = data.dimensionValues?.additionalData?.find((d) => d.objectName === key);

    // We prefer to use the `additionalData` value if it exists since it has more info (like name and externalId), but fall back to the string value if not
    // This helps avoid making unnecessary calls to fetch dimension details when we already have the info in `additionalData`
    if (additionalDataValue) {
      dimensions.set(key, { id: valueAsStr, name: additionalDataValue.name, externalId: additionalDataValue.externalId });
    } else {
      dimensions.set(key, valueAsStr);
    }
  }

  for (const [key, value] of Object.entries(data.dimensionValues?.additionalValues ?? {})) {
    if (!value) continue;
    const additionalDataValue = data.dimensionValues?.additionalData?.find((d) => d.objectName === key);
    dimensions.set(
      key,
      additionalDataValue ? { id: value, name: additionalDataValue.name, externalId: additionalDataValue.externalId } : value,
    );
  }

  return {
    dimensions,
    billable,
  };
}

export function getTimesheetRowTotalDuration(row: { entries: { duration?: number | null }[] }): number {
  return row.entries.reduce((total, entry) => total + (entry.duration ?? 0), 0);
}
